import * as React from 'react';
import { FC, useState, useEffect, useReducer, useId } from 'react';
import { sample } from 'lodash';
import type { CampaignView, JackpotView } from 'app/CasinoGames/useJackpotAggregatorCampaignViews';
import { moneyStr } from 'app/helpers/money';
import locales from 'app/CasinoGames/locales';

export const CAMPAIGN_SHOW_TIME = 300000;
export const JACKPOT_SHOW_TIME = 5000;

const JackpotViewComponent: FC<{ jackpotView: JackpotView; currency: string }> = ({ jackpotView, currency }) => {
  const id = useId();

  return (
    <div aria-labelledby={id} className="c-jackpot_wrap">
      <div className="c-jackpot_image" />
      <div id={id} className="c-jackpot_name">
        {jackpotView.name}
      </div>
      <div className="c-jackpot_amount">{moneyStr(jackpotView.current_value, currency)}</div>
    </div>
  );
};

export type JackpotViewsProps = { campaignViews: CampaignView[] };

let timer: NodeJS.Timeout;

const wait = (ms: number) =>
  new Promise((resolve) => {
    timer = setTimeout(resolve, ms);
  });

const Jackpots: FC<JackpotViewsProps> = ({ campaignViews }) => {
  const [campaignView, setCampaignView] = useState<CampaignView | undefined>();
  const [jackpotView, setJackpotView] = useState<JackpotView>();
  const [campaignSequence, campaignShown] = useReducer((x) => x + 1, 0);
  const l = locales();

  const showJackpots = async () => {
    await wait(CAMPAIGN_SHOW_TIME);

    const currentCampaignView = sample(campaignViews);

    if (currentCampaignView) {
      setCampaignView(currentCampaignView);

      for (let index = 0; index < currentCampaignView.jackpot_views.length; index++) {
        setJackpotView(currentCampaignView.jackpot_views[index]);
        // eslint-disable-next-line no-await-in-loop
        await wait(JACKPOT_SHOW_TIME);
      }

      setCampaignView(undefined);
      setJackpotView(undefined);
    }

    campaignShown();
  };

  useEffect(() => {
    showJackpots();

    return () => {
      clearTimeout(timer);
    };
  }, [campaignSequence]);

  if (!campaignView || !jackpotView) return null;

  return (
    <div className="campaign-jackpot c-jackpot">
      <JackpotViewComponent jackpotView={jackpotView} currency={campaignView.currency} />
      {campaignView.url && (
        /* eslint-disable-next-line react/jsx-no-target-blank */
        <a className="c-jackpot_link" href={`/promo${campaignView.url}`} target="_blank" rel="opener">
          {l.jackpot_aggregator_campaigns_details}
          <span>&rang;</span>
        </a>
      )}
    </div>
  );
};

export default Jackpots;
